import axios, { GenericAbortSignal } from 'axios';

import { axiosRestApi } from 'config/axiosRestApi';
import { TECHNOLOGIES, TECHNOLOGIES_BY_GROUP } from 'constants/products';
import { logError } from 'utils/logError';
import { Technology } from 'utils/products';
import { ISignedUrlsResponse, IUploadExamFiles } from './dropzone.types';

const uploadImageFiles = async (
  files: File[],
  {
    examId,
    institutionId,
  }: {
    examId?: string;
    institutionId?: string;
  },
  signal?: GenericAbortSignal,
) => {
  // request new upload url
  const payloadFiles = files.map((file) => ({
    contentType: file.type,
    fileName: file.name,
  }));
  const response = await axiosRestApi.post<ISignedUrlsResponse>(
    '/uploads/signed-urls',
    {
      files: payloadFiles,
      examId: examId ?? '',
      institution: institutionId,
      technology: Technology.availableTechnology,
    },
  );
  const urls = response.data.urls;

  let contentType = 'application/octet-stream';

  const chunks = 4;
  for (let i = 0; i < files.length; i += chunks) {
    const chunk = files.slice(i, i + chunks);
    await Promise.all(
      chunk.map((file, index) => {
        const uploadRequest = axios.create({
          // use baseURL (generated by get_signed_url request)
          baseURL: urls[i + index],
        });
        if (file.type === 'image/jpeg' || file.type === 'image/png') {
          contentType = file.type;
        }

        return uploadRequest.put('', file, {
          signal,
          headers: {
            'Content-Type': contentType,
          },
        });
      }),
    );
  }

  // # Upload eot file to signalize the end of transfer
  // # Required when more than one file can be uploaded
  const endOfTransferFile = new File([], '_eot', {
    type: 'text/plain',
  });
  const eotUploadRequest = axios.create({
    // use baseURL (generated by get_signed_url request)
    baseURL: urls[urls.length - 1],
  });
  await eotUploadRequest.put('', endOfTransferFile, {
    signal,
    headers: { 'Content-Type': 'application/octet-stream' },
  });
};

const uploadVideoFiles = async (
  files: File[],
  {
    examId,
    institutionId,
  }: {
    examId?: string;
    institutionId?: string;
  },
  signal?: GenericAbortSignal,
) => {
  // request new upload url
  const payloadFiles = files.map((file) => ({
    contentType: 'application/octet-stream',
    fileName: file.name,
  }));
  const response = await axiosRestApi.post<ISignedUrlsResponse>(
    '/uploads/signed-urls',
    {
      files: payloadFiles,
      examId: examId ?? '',
      institution: institutionId,
      technology: Technology.availableTechnology,
    },
  );
  // set generated URL as upload URL request
  // only one video file per exam is allowed
  const url = response.data.urls[0];
  const uploadRequest = axios.create({
    // use baseURL (generated by get_signed_url request)
    baseURL: url,
  });

  await uploadRequest.put('', files[0], {
    signal,
    headers: {
      'Content-Type': 'application/octet-stream',
    },
  });
};

/**
 * Uploads exam files to the API.
 * @param files - The files to upload.
 * @param examId - The exam ID.
 * @param institutionId - The institution ID.
 * @param uploadControllerRef - The reference to the upload controller.
 */
export const onUploadSubmit = async ({
  files,
  examId,
  institutionId,
  uploadControllerRef,
}: IUploadExamFiles) => {
  if (!files || files.length === 0) {
    logError({ error: 'FILE NOT FOUND!!!', ignoreSentry: true });
    return;
  }

  try {
    if (Technology.isAvailable(TECHNOLOGIES_BY_GROUP.detection)) {
      await uploadImageFiles(
        files,
        {
          examId,
          institutionId,
        },
        uploadControllerRef?.current?.signal,
      );
    }
    if (Technology.isAvailable(TECHNOLOGIES.deepCapsule)) {
      await uploadVideoFiles(
        files,
        {
          examId,
          institutionId,
        },
        uploadControllerRef?.current?.signal,
      );
    }
  } catch (err) {
    if (
      err instanceof Error &&
      (err.name === 'AbortError' || err.name === 'CanceledError')
    ) {
      throw new Error('Upload canceled');
    }
    throw err;
  }
};
